import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { toast } from 'sonner'
import { ArrowUpRightIcon, HelpCircleIcon, PlusIcon, SearchLgIcon } from '@untitledui-icons/react/line'

import { PaginationTable } from '@/components/application/pagination/pagination'
import { Table } from '@/components/application/table/table'
import { EmptyState, LoadingSpinner } from '@/components/common'
import { Input } from '@/components/ui/Input'
import {
  useWidgetOverview,
  useWidgetTemplates,
  type WidgetOverviewRow,
} from '@/hooks/api/useWidgets'
import { usePermissions } from '@/hooks/api/usePermissions'
import { WidgetEmbedCell } from './WidgetEmbedCell'
import { WidgetEmbedModal } from './WidgetEmbedModal'
import { WidgetStatusBadge } from './WidgetStatusBadge'
import { WidgetTemplateMenu } from './WidgetTemplateMenu'
import { WidgetTemplatePickerModal } from './WidgetTemplatePickerModal'
import { WidgetWhatsAppSignupModal } from './WidgetWhatsAppSignupModal'

const PAGE_SIZE = 25

function rowMatchesQuery(row: WidgetOverviewRow, query: string): boolean {
  const haystack = [row.assistant_name, row.customer_name, row.phone_number, row.template_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

export function WidgetAssistantsTab() {
  const { t } = useTranslation()
  const base = 'resources.widgets.assistants'

  const { data: rows, isLoading } = useWidgetOverview()
  const { data: templates } = useWidgetTemplates()
  const { isAdmin, isPartner } = usePermissions()
  const showCustomerContext = isAdmin || isPartner
  const [pickerRow, setPickerRow] = useState<WidgetOverviewRow | null>(null)
  const [embedRow, setEmbedRow] = useState<WidgetOverviewRow | null>(null)
  const [signupRow, setSignupRow] = useState<WidgetOverviewRow | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const items = rows ?? []
  const query = search.trim().toLowerCase()
  const filtered = useMemo(
    () => (query ? items.filter((row) => rowMatchesQuery(row, query)) : items),
    [items, query]
  )
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [query])

  const handleCopyEmbed = async (row: WidgetOverviewRow) => {
    if (!row.embed_snippet) return

    try {
      await navigator.clipboard.writeText(row.embed_snippet)
      toast.success(t('resources.widgets.embed.copied_toast'))
    } catch {
      toast.error(t('resources.widgets.embed.copy_failed_toast'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <EmptyState title={t(`${base}.empty_title`)} description={t(`${base}.empty_description`)} />
    )
  }

  return (
    <>
      <div className="w-full max-w-[1200px] overflow-visible rounded-xl">
        <div className="mb-4 max-w-sm">
          <Input
            type="search"
            value={search}
            onChange={setSearch}
            aria-label={t(`${base}.search_label`)}
            placeholder={t(`${base}.search_placeholder`)}
            leftIcon={<SearchLgIcon className="h-4 w-4" />}
          />
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title={t(`${base}.no_matches_title`)}
            description={t(`${base}.no_matches_description`, { query: search.trim() })}
          />
        ) : (
          <>
        <Table aria-label={t(`${base}.table_label`)}>
          <Table.Header
            bordered={false}
            className="bg-gray-100 dark:bg-gray-800 [&_span]:text-gray-500 dark:[&_span]:text-gray-400"
          >
            <Table.Head
              id="assistant"
              label={t(`${base}.columns.assistant`)}
              className="w-80"
              isRowHeader
            />
            {showCustomerContext && (
              <Table.Head
                id="customer"
                label={t(`${base}.columns.customer`)}
                className="w-52"
              />
            )}
            <Table.Head
              id="number"
              label={t(`${base}.columns.number`)}
              className="w-[170px] [&>div]:justify-center"
            />
            <Table.Head
              id="status"
              label={t(`${base}.columns.status`)}
              className="w-[170px] [&>div]:justify-center"
            />
            <Table.Head
              id="template"
              label={t(`${base}.columns.template`)}
              className="w-[170px] [&>div]:justify-center"
            />
            <Table.Head
              id="embed"
              label={t(`${base}.columns.embed`)}
              className="w-[170px] [&>div]:justify-center"
            >
              <EmbedCodeColumnHint />
            </Table.Head>
          </Table.Header>

          <AriaTableBody>
            {pageRows.map((row) => {
              const whatsappNumber =
                row.connection_state === 'not_connected' ? null : row.phone_number

              return (
                <Table.Row
                  key={row.assistant_id}
                  id={row.assistant_id}
                  className="hover:bg-transparent dark:hover:bg-transparent [&>td]:after:hidden"
                >
                  <Table.Cell>
                    <span className="block text-base font-medium text-gray-900 dark:text-gray-100">
                      {row.assistant_name}
                    </span>
                  </Table.Cell>

                  {showCustomerContext && (
                    <Table.Cell>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {row.customer_name}
                      </span>
                    </Table.Cell>
                  )}

                  <Table.Cell className="px-3 text-center">
                    {whatsappNumber ? (
                      <a
                        href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t(`${base}.open_chat`, { assistant: row.assistant_name })}
                        className="bg-success-25 hover:bg-success-50 dark:hover:bg-success-950 inline-flex h-7 items-center gap-1 rounded-[2px] border border-gray-50 px-1.5 font-mono text-xs tracking-[-0.5px] text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      >
                        {whatsappNumber}
                        <ArrowUpRightIcon className="size-4 shrink-0" />
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setSignupRow(row)}
                        className="inline-flex h-7 items-center gap-1.5 rounded-sm px-1.5 text-[13px] text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                      >
                        <PlusIcon className="size-4 shrink-0" />
                        {t(`${base}.connect`)}
                      </button>
                    )}
                  </Table.Cell>

                  <Table.Cell className="px-3 text-center">
                    <WidgetStatusBadge
                      hasTemplate={!!row.template_id}
                      websiteLive={row.website_live}
                    />
                  </Table.Cell>

                  <Table.Cell className="px-3 text-center">
                    <WidgetTemplateMenu
                      row={row}
                      templates={templates ?? []}
                      onSeeAll={() => setPickerRow(row)}
                    />
                  </Table.Cell>

                  <Table.Cell className="px-3 text-center">
                    <WidgetEmbedCell
                      assistantName={row.assistant_name}
                      snippet={row.embed_snippet}
                      onView={() => setEmbedRow(row)}
                      onCopy={() => void handleCopyEmbed(row)}
                    />
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </AriaTableBody>
        </Table>
            {totalPages > 1 && (
              <PaginationTable
                page={safePage}
                total={totalPages}
                perPage={PAGE_SIZE}
                onPageChange={setPage}
                showPerPageSelector={false}
              />
            )}
          </>
        )}
      </div>

      <WidgetTemplatePickerModal
        row={pickerRow}
        templates={templates ?? []}
        onClose={() => setPickerRow(null)}
      />

      <WidgetEmbedModal row={embedRow} onClose={() => setEmbedRow(null)} />
      <WidgetWhatsAppSignupModal row={signupRow} onClose={() => setSignupRow(null)} />
    </>
  )
}

/** Column hint matching the Figma hover tip; portaled so the table cannot clip it. */
function EmbedCodeColumnHint() {
  const { t } = useTranslation()
  const base = 'resources.widgets.assistants'
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const rect = triggerRef.current?.getBoundingClientRect()

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t(`${base}.columns.embed_tooltip_aria`)}
        aria-describedby={open ? 'embed-code-column-hint' : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="cursor-help text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <HelpCircleIcon className="size-4" />
      </button>
      {open && rect
        ? createPortal(
            <div
              id="embed-code-column-hint"
              role="tooltip"
              className="pointer-events-none fixed z-50 w-[170px] rounded-md bg-gray-950 px-3 py-2 text-left text-xs leading-[18px] font-medium text-white shadow-lg"
              style={{
                top: rect.top - 10,
                left: rect.right + 6,
                transform: 'translate(-100%, -100%)',
              }}
            >
              {t(`${base}.columns.embed_tooltip`)}
              <span
                aria-hidden
                className="absolute top-full right-3 border-[5px] border-transparent border-t-gray-950"
              />
            </div>,
            document.body
          )
        : null}
    </>
  )
}
